// src/components/AdminDashboard/sections/UserFootageSection.jsx
import React, { useState, useEffect } from 'react';
import {
  Typography,
  CircularProgress,
  Alert,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Box,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { 
  SectionCard, 
  SectionHeader, 
  TableWrapper, 
  StyledTable, 
  StyledTableCell, 
  NoDataMessage
} from '../AdminDashboard.styles';
import { COLORS } from '../../../../BrandColors';
import { useAdminUsers } from '../../../../hooks/useAdminUsers';
import Pagination from '../../../Video/videoList/Pagination';

const UserFootageSection = () => {
  // Use Redux hook for footage hours data
  const {
    footageHours,
    footageHoursLoading,
    footageHoursError,
    footageHoursPage,
    footageHoursRowsPerPage,
    footageHoursTotalElements,
    footageHoursTotalPages,
    loadUserFootagePurchaseHours,
    handleFootageHoursPageChange,
    handleFootageHoursRowsPerPageChange
  } = useAdminUsers();

  // Local state for page management (convert from 0-based to 1-based)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Sync local state with Redux state
  useEffect(() => {
    setCurrentPage(footageHoursPage + 1);
    setPageSize(footageHoursRowsPerPage);
  }, [footageHoursPage, footageHoursRowsPerPage]);

  // Load footage hours data
  useEffect(() => {
    loadUserFootagePurchaseHours({
      page: footageHoursPage,
      size: footageHoursRowsPerPage,
      sort: 'hours,desc'
    });
  }, [loadUserFootagePurchaseHours, footageHoursPage, footageHoursRowsPerPage]);

  const handlePageChange = (page) => {
    handleFootageHoursPageChange(page - 1); // Convert to 0-based
  };

  const handlePageSizeChange = (newSize) => {
    handleFootageHoursRowsPerPageChange(parseInt(newSize, 10));
  };

  const formatHours = (hours) => {
    if (hours === null || hours === undefined) return '0 hours';
    
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    
    if (minutes === 0) {
      return `${wholeHours} hour${wholeHours !== 1 ? 's' : ''}`;
    } else {
      return `${wholeHours}h ${minutes}m`;
    }
  };

  const getHoursColor = (hours) => {
    if (hours >= 10) return COLORS.accentTeal;
    if (hours >= 5) return COLORS.accentOrange;
    return COLORS.accentBlueGray;
  };

  const getTotalHours = () => {
    return footageHours.reduce((total, user) => total + (user.hours || 0), 0);
  };

  const getAverageHours = () => {
    if (footageHours.length === 0) return 0;
    return getTotalHours() / footageHours.length;
  };

  return (
    <SectionCard>
      <SectionHeader>
        <Typography variant="h6">User Footage Hours</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Total Users: {footageHoursTotalElements}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Page Size</InputLabel>
            <Select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              label="Page Size"
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </SectionHeader>

      {footageHoursLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {footageHoursError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {footageHoursError}
        </Alert>
      )}

      {/* Summary Statistics */}
      {footageHours.length > 0 && (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
          gap: 2, 
          mb: 3,
          p: 2,
          background: `linear-gradient(135deg, ${COLORS.accentTeal}10 0%, ${COLORS.accentOrange}10 100%)`,
          borderRadius: 2,
          border: `1px solid ${COLORS.accentTeal}30`
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
              {formatHours(getTotalHours())}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Hours
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
              {formatHours(getAverageHours())}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Average per User
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
              {footageHours.filter(user => user.hours >= 10).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Heavy Users (10+ hrs)
            </Typography>
          </Box>
        </Box>
      )}

      {footageHours.length === 0 && !footageHoursLoading ? (
        <NoDataMessage>
          No footage data available
        </NoDataMessage>
      ) : (
        <>
            <TableWrapper>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Rank</StyledTableCell>
                    <StyledTableCell>User ID</StyledTableCell>
                    <StyledTableCell>Email</StyledTableCell>
                    <StyledTableCell>Total Hours</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {footageHours.map((user, index) => (
                    <TableRow key={user.userId}>
                      <TableCell>
                        <Chip 
                          label={`#${(currentPage - 1) * pageSize + index + 1}`}
                          size="small"
                          sx={{ 
                            backgroundColor: COLORS.accentTeal,
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell>{user.userId}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: getHoursColor(user.hours)
                          }}
                        >
                          {formatHours(user.hours)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={user.hours >= 10 ? 'Heavy User' : user.hours >= 5 ? 'Active User' : 'Light User'}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            borderColor: getHoursColor(user.hours),
                            color: getHoursColor(user.hours),
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </TableWrapper>

          {footageHoursTotalPages > 1 && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                currentPage={currentPage}
                totalPages={footageHoursTotalPages}
                onPageChange={handlePageChange}
              />
            </Box>
          )}
        </>
      )}
    </SectionCard>
  );
};

export default UserFootageSection; 